import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface LocalRecoloredLogo {
  logoId: string;
  blobUrl: string;
  palette: { originalColor: string; recoloredColor?: string; name?: string }[];
  timestamp: number;
  isProcessing: boolean;
  localRecoloredId: string;
  // Vector-specific fields
  vectorSvgBlobUrl?: string;
  recoloredSvgContent?: string;
}

interface LocalRecoloredLogosState {
  currentLogo: LocalRecoloredLogo | null;
  debounceTimer: NodeJS.Timeout | null;
}

const initialState: LocalRecoloredLogosState = {
  currentLogo: null,
  debounceTimer: null,
};

const localRecoloredLogosSlice = createSlice({
  name: 'localRecoloredLogos',
  initialState,
  reducers: {
    setLocalRecoloredLogo: (state, action: PayloadAction<LocalRecoloredLogo>) => {
      // Clean up previous logo if exists
      if (state.currentLogo && state.currentLogo.logoId !== action.payload.logoId) {
        // Revoke previous blob URLs to free memory
        if (state.currentLogo.blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.blobUrl);
        }
        if (state.currentLogo.vectorSvgBlobUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.vectorSvgBlobUrl);
        }
      }
      state.currentLogo = action.payload;
    },
    clearLocalRecoloredLogo: (
      state,
      action: PayloadAction<{ logoId: string; recoloredId: string }>,
    ) => {
      if (
        state.currentLogo &&
        state.currentLogo.logoId === action.payload.logoId &&
        state.currentLogo.localRecoloredId === action.payload.recoloredId
      ) {
        // Revoke blob URLs to free memory
        if (state.currentLogo.blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.blobUrl);
        }
        if (state.currentLogo.vectorSvgBlobUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.vectorSvgBlobUrl);
        }
        state.currentLogo = null;
      }
    },
    clearLocalRecoloredLogoByLogoId: (state, action: PayloadAction<string>) => {
      if (state.currentLogo && state.currentLogo.logoId === action.payload) {
        // Revoke blob URLs to free memory
        if (state.currentLogo.blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.blobUrl);
        }
        if (state.currentLogo.vectorSvgBlobUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.vectorSvgBlobUrl);
        }
        state.currentLogo = null;
      }
    },
    setDebounceTimer: (state, action: PayloadAction<NodeJS.Timeout | null>) => {
      // Clear previous timer if exists
      if (state.debounceTimer) {
        clearTimeout(state.debounceTimer);
      }
      state.debounceTimer = action.payload;
    },
    clearAll: state => {
      // Clean up current logo
      if (state.currentLogo) {
        if (state.currentLogo.blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.blobUrl);
        }
        if (state.currentLogo.vectorSvgBlobUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(state.currentLogo.vectorSvgBlobUrl);
        }
        state.currentLogo = null;
      }
      // Clear timer
      if (state.debounceTimer) {
        clearTimeout(state.debounceTimer);
        state.debounceTimer = null;
      }
    },
  },
});

export const {
  setLocalRecoloredLogo,
  clearLocalRecoloredLogo,
  clearLocalRecoloredLogoByLogoId,
  setDebounceTimer,
  clearAll,
} = localRecoloredLogosSlice.actions;

export const localRecoloredLogosReducer = localRecoloredLogosSlice.reducer;
